import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { api } from '@/lib/api';
import { formatCurrency, calculateWeeks, generateMonthlyInvoices } from '@/lib/dateUtils';
import { PROJECT_TYPES, PROJECT_STATUSES } from '@/lib/constants';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Edit2, Check, X } from 'lucide-react';
import { BarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';

const ELIGIBLE_STATUSES = ['sow', 'execute', 'closed'];

// Financial year runs Apr → Mar. Given a FY start year (e.g. 2026 means Apr 2026 – Mar 2027)
const getFYMonths = (fyStartYear) => {
  const months = [];
  for (let m = 3; m < 15; m++) { // 3=Apr(index) .. 14=Mar next year
    const year = fyStartYear + Math.floor(m / 12);
    const month = m % 12; // 0-indexed
    const d = new Date(year, month, 1);
    months.push({
      key: `${year}-${String(month + 1).padStart(2, '0')}`,
      label: d.toLocaleDateString('en-US', { month: 'short' }),
      fullLabel: d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }),
    });
  }
  return months;
};

const getFYLabel = (yr) => `FY ${yr}-${String(yr + 1).slice(-2)}`;

const getCurrentFYStartYear = () => {
  const now = new Date();
  return now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-700 rounded-lg p-3 shadow-xl">
      <p className="font-manrope text-xs text-zinc-400 mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} className="font-mono text-sm" style={{ color: p.fill || p.color }}>
          {p.name}: {formatCurrency(p.value)}
        </p>
      ))}
    </div>
  );
};

export function MonthlyConsumptionSlide({ projects, adhocInvoices, selectedClients }) {
  const currentFY = getCurrentFYStartYear();
  const [selectedFY, setSelectedFY] = useState(() => {
    // Default to FY 2026-27 (start year 2026)
    const now = new Date();
    const fy = now.getMonth() < 3 ? now.getFullYear() - 1 : now.getFullYear();
    return Math.max(fy, 2026);
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    // Default to April of the selected FY
    return '2026-04';
  });
  const [notes, setNotes] = useState({ opportunities: [], risks: [] });
  const [editingOpp, setEditingOpp] = useState(null);
  const [editingRisk, setEditingRisk] = useState(null);
  const [newOpp, setNewOpp] = useState('');
  const [newRisk, setNewRisk] = useState('');
  const [notesDirty, setNotesDirty] = useState(false);
  const [backendConsumptionTrend, setBackendConsumptionTrend] = useState(null); // Backend-calculated consumption trend

  // Derive available FY options from project data
  const fyOptions = useMemo(() => {
    const years = new Set();
    projects.forEach(p => {
      if (p.startDate) {
        const d = new Date(p.startDate);
        const fy = d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear();
        years.add(fy);
      }
      if (p.endDate) {
        const d = new Date(p.endDate);
        const fy = d.getMonth() < 3 ? d.getFullYear() - 1 : d.getFullYear();
        years.add(fy);
      }
    });
    years.add(currentFY);
    years.add(2026); // Always include FY 2026-27
    return [...years].sort();
  }, [projects, currentFY]);

  const fyMonths = useMemo(() => getFYMonths(selectedFY), [selectedFY]);

  // Month options for selected FY
  const monthOptions = useMemo(() => {
    return fyMonths.map(m => ({ value: m.key, label: m.fullLabel }));
  }, [fyMonths]);

  // Load notes for selected month
  const loadNotes = useCallback(async () => {
    try {
      const data = await api.getNotes(selectedMonth);
      setNotes({ opportunities: data.opportunities || [], risks: data.risks || [] });
      setNotesDirty(false);
    } catch {
      setNotes({ opportunities: [], risks: [] });
    }
  }, [selectedMonth]);

  useEffect(() => { loadNotes(); }, [loadNotes]);

  const saveNotes = async () => {
    try {
      await api.saveNotes(selectedMonth, { monthKey: selectedMonth, opportunities: notes.opportunities, risks: notes.risks });
      toast.success('Notes saved');
      setNotesDirty(false);
    } catch {
      toast.error('Failed to save notes');
    }
  };

  // Helper: get consumption for a given monthKey from projects
  const getMonthData = useCallback((monthKey) => {
    let totalConsumption = 0;
    let resourceCount = 0;
    const resourceSet = new Set();

    projects.forEach(p => {
      if (!p.startDate || !p.endDate || !ELIGIBLE_STATUSES.includes(p.status)) return;
      if (selectedClients.length > 0 && !selectedClients.includes(p.clientName)) return;
      const numWeeks = calculateWeeks(p.startDate, p.endDate);
      const generated = generateMonthlyInvoices(p.startDate, p.endDate, p.resources || [], numWeeks);
      const inv = generated.find(g => g.monthKey === monthKey);
      if (inv) {
        const saved = (p.invoices || []).find(si => si.monthKey === monthKey);
        totalConsumption += saved?.consumptionAmount !== undefined ? saved.consumptionAmount : inv.consumptionAmount;
        (p.resources || []).forEach(r => {
          const allocIdx = generated.findIndex(g => g.monthKey === monthKey);
          if (allocIdx >= 0) {
            // Check if resource has allocation in this month's weeks
            const weekStart = allocIdx * 4;
            const weekEnd = Math.min(weekStart + 4, (r.weeklyAllocation || []).length);
            let hasAlloc = false;
            for (let w = weekStart; w < weekEnd; w++) {
              if ((r.weeklyAllocation || [])[w] > 0) { hasAlloc = true; break; }
            }
            if (hasAlloc) resourceSet.add(`${p.id}-${r.type}`);
          }
        });
      }
    });

    // Add ad-hoc
    adhocInvoices.forEach(a => {
      if (a.monthKey === monthKey) {
        if (selectedClients.length > 0 && !selectedClients.includes(a.clientName)) return;
        totalConsumption += (a.consumptionAmount ?? a.invoiceValue ?? 0);
      }
    });

    resourceCount = resourceSet.size;
    return { consumption: totalConsumption, resources: resourceCount };
  }, [projects, adhocInvoices, selectedClients]);

  // --- TOP LEFT: Current vs Next Month comparison ---
  const comparisonData = useMemo(() => {
    const [y, m] = selectedMonth.split('-').map(Number);
    const currKey = selectedMonth;
    const nextDate = new Date(y, m, 1); // m is already 1-indexed, so new Date(y, m, 1) = next month
    const nextKey = `${nextDate.getFullYear()}-${String(nextDate.getMonth() + 1).padStart(2, '0')}`;
    const currLabel = new Date(y, m - 1, 1).toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const nextLabel = nextDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    const curr = getMonthData(currKey);
    const next = getMonthData(nextKey);
    return { curr: { ...curr, label: currLabel }, next: { ...next, label: nextLabel } };
  }, [selectedMonth, getMonthData]);

  // --- TOP RIGHT: YoY consumption trend for full FY ---
  const yoyChartData = useMemo(() => {
    return fyMonths.map(m => {
      const data = getMonthData(m.key);
      return { month: m.label, monthKey: m.key, consumption: data.consumption };
    });
  }, [fyMonths, getMonthData]);

  const yoyTotal = useMemo(() => yoyChartData.reduce((s, d) => s + d.consumption, 0), [yoyChartData]);

  // --- BOTTOM RIGHT: Revenue by project type based on monthly consumption within the FY ---
  const revenueByTypeData = useMemo(() => {
    const typeMap = {};
    const fyMonthKeys = new Set(fyMonths.map(m => m.key));

    projects.forEach(p => {
      if (!ELIGIBLE_STATUSES.includes(p.status)) return;
      if (selectedClients.length > 0 && !selectedClients.includes(p.clientName)) return;
      if (!p.startDate || !p.endDate) return;

      const pType = p.projectType || 'staff_aug';
      const numWeeks = calculateWeeks(p.startDate, p.endDate);
      const generated = generateMonthlyInvoices(p.startDate, p.endDate, p.resources || [], numWeeks);

      generated.forEach(inv => {
        // Only count months that fall within this FY (Apr-Mar)
        if (!fyMonthKeys.has(inv.monthKey)) return;
        const saved = (p.invoices || []).find(si => si.monthKey === inv.monthKey);
        const consumption = saved?.consumptionAmount !== undefined ? saved.consumptionAmount : inv.consumptionAmount;
        if (!typeMap[pType]) typeMap[pType] = 0;
        typeMap[pType] += consumption;
      });
    });

    // Also include ad-hoc invoices within the FY months
    adhocInvoices.forEach(a => {
      if (!fyMonthKeys.has(a.monthKey)) return;
      if (selectedClients.length > 0 && !selectedClients.includes(a.clientName)) return;
      // Find project type from linked project
      const proj = projects.find(p => p.id === a.projectId);
      if (proj && !ELIGIBLE_STATUSES.includes(proj.status)) return;
      const pType = proj?.projectType || 'staff_aug';
      if (!typeMap[pType]) typeMap[pType] = 0;
      typeMap[pType] += (a.consumptionAmount ?? a.invoiceValue ?? 0);
    });

    return typeMap;
  }, [projects, adhocInvoices, fyMonths, selectedClients]);

  const totalRevenue = useMemo(() => Object.values(revenueByTypeData).reduce((s, v) => s + v, 0), [revenueByTypeData]);

  const typeChartData = useMemo(() => {
    const typeLabels = { delivery: 'Delivery', ops_support: 'Ops Support', staff_aug: 'Staff Aug' };
    return Object.entries(typeLabels).map(([key, label]) => ({
      type: label,
      revenue: revenueByTypeData[key] || 0,
      fill: key === 'delivery' ? '#A80097' : key === 'ops_support' ? '#6366f1' : '#f59e0b',
    }));
  }, [revenueByTypeData]);

  // Note editing helpers
  const addOpp = () => {
    if (!newOpp.trim()) return;
    setNotes(prev => ({ ...prev, opportunities: [...prev.opportunities, newOpp.trim()] }));
    setNewOpp('');
    setNotesDirty(true);
  };
  const addRisk = () => {
    if (!newRisk.trim()) return;
    setNotes(prev => ({ ...prev, risks: [...prev.risks, newRisk.trim()] }));
    setNewRisk('');
    setNotesDirty(true);
  };
  const removeOpp = (idx) => { setNotes(prev => ({ ...prev, opportunities: prev.opportunities.filter((_, i) => i !== idx) })); setNotesDirty(true); };
  const removeRisk = (idx) => { setNotes(prev => ({ ...prev, risks: prev.risks.filter((_, i) => i !== idx) })); setNotesDirty(true); };
  const updateOpp = (idx, val) => { setNotes(prev => { const o = [...prev.opportunities]; o[idx] = val; return { ...prev, opportunities: o }; }); setNotesDirty(true); };
  const updateRisk = (idx, val) => { setNotes(prev => { const r = [...prev.risks]; r[idx] = val; return { ...prev, risks: r }; }); setNotesDirty(true); };

  const selectedMonthLabel = monthOptions.find(m => m.value === selectedMonth)?.label || selectedMonth;

  return (
    <div data-testid="consumption-slide">
      {/* Filters */}
      <div className="flex items-center gap-4 mb-5">
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Financial Year</span>
          <Select value={String(selectedFY)} onValueChange={v => { const fy = Number(v); setSelectedFY(fy); setSelectedMonth(`${fy}-04`); }}>
            <SelectTrigger data-testid="fy-select" className="w-40 bg-zinc-950 border-zinc-800 text-sm h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              {fyOptions.map(yr => (
                <SelectItem key={yr} value={String(yr)} className="text-zinc-200 text-xs focus:bg-zinc-900">{getFYLabel(yr)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Month</span>
          <Select value={selectedMonth} onValueChange={setSelectedMonth}>
            <SelectTrigger data-testid="month-select" className="w-48 bg-zinc-950 border-zinc-800 text-sm h-8">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-950 border-zinc-800">
              {monthOptions.map(m => (
                <SelectItem key={m.value} value={m.value} className="text-zinc-200 text-xs focus:bg-zinc-900">{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* 4 Quadrant Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* TOP LEFT: Comparison Table */}
        <Card className="bg-zinc-950 border border-zinc-700/50 rounded-xl" data-testid="comparison-card">
          <CardContent className="p-4">
            <h3 className="font-manrope font-bold text-sm text-white mb-3">Monthly Snapshot</h3>
            <Table>
              <TableHeader>
                <TableRow className="border-zinc-800 hover:bg-transparent">
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope">Metric</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope text-right">{comparisonData.curr.label}</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope text-right">{comparisonData.next.label}</TableHead>
                  <TableHead className="text-xs uppercase tracking-widest text-zinc-500 font-manrope text-right">Variance</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <TableRow className="border-zinc-900 hover:bg-zinc-900/30">
                  <TableCell className="font-manrope text-sm text-zinc-300">Total Consumption</TableCell>
                  <TableCell className="text-right font-mono text-sm text-white">{formatCurrency(comparisonData.curr.consumption)}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-white">{formatCurrency(comparisonData.next.consumption)}</TableCell>
                  <TableCell className="text-right">
                    {(() => {
                      const diff = comparisonData.next.consumption - comparisonData.curr.consumption;
                      return <span className={`font-mono text-sm ${diff > 0 ? 'text-emerald-400' : diff < 0 ? 'text-red-400' : 'text-zinc-400'}`}>{diff > 0 ? '+' : ''}{formatCurrency(diff)}</span>;
                    })()}
                  </TableCell>
                </TableRow>
                <TableRow className="border-zinc-900 hover:bg-zinc-900/30">
                  <TableCell className="font-manrope text-sm text-zinc-300">Resources Deployed</TableCell>
                  <TableCell className="text-right font-mono text-sm text-white">{comparisonData.curr.resources}</TableCell>
                  <TableCell className="text-right font-mono text-sm text-white">{comparisonData.next.resources}</TableCell>
                  <TableCell className="text-right">
                    {(() => {
                      const diff = comparisonData.next.resources - comparisonData.curr.resources;
                      return <span className={`font-mono text-sm ${diff > 0 ? 'text-emerald-400' : diff < 0 ? 'text-red-400' : 'text-zinc-400'}`}>{diff > 0 ? '+' : ''}{diff}</span>;
                    })()}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* TOP RIGHT: YoY Consumption Trend */}
        <Card className="bg-zinc-950 border border-zinc-700/50 rounded-xl" data-testid="yoy-chart-card">
          <CardContent className="p-4">
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={yoyChartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="month" tick={{ fill: '#a1a1aa', fontSize: 10, fontFamily: 'Manrope' }} axisLine={{ stroke: '#3f3f46' }} />
                  <YAxis tick={{ fill: '#a1a1aa', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#3f3f46' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="consumption" name="Consumption" fill="#A80097" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-center mt-3">
              <h3 className="font-manrope font-bold text-sm text-zinc-400">{getFYLabel(selectedFY)} Consumption Trend</h3>
              <p className="font-mono text-lg font-bold text-white mt-1">{formatCurrency(yoyTotal)}</p>
            </div>
          </CardContent>
        </Card>

        {/* BOTTOM LEFT: Opportunities & Risks */}
        <Card className="bg-zinc-950 border border-zinc-700/50 rounded-xl" data-testid="notes-card">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-manrope font-bold text-sm text-white">Opportunities & Risks — {selectedMonthLabel}</h3>
              <Button size="sm" variant="ghost" onClick={saveNotes} disabled={!notesDirty} data-testid="save-notes-btn" className={`h-7 text-xs ${notesDirty ? 'text-brand-400 hover:text-brand-300' : 'text-zinc-600'}`}>
                <Save className="w-3.5 h-3.5 mr-1" /> Save
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {/* Opportunities */}
              <div>
                <p className="text-xs uppercase tracking-widest text-emerald-400 font-manrope mb-2">Opportunities</p>
                <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                  {notes.opportunities.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-1.5 group">
                      <span className="text-emerald-500 text-xs mt-0.5 shrink-0">&#8226;</span>
                      {editingOpp === idx ? (
                        <div className="flex-1 flex items-center gap-1">
                          <Input value={item} onChange={e => updateOpp(idx, e.target.value)} className="bg-zinc-900 border-zinc-700 text-xs h-6 py-0" autoFocus onKeyDown={e => e.key === 'Enter' && setEditingOpp(null)} />
                          <button onClick={() => setEditingOpp(null)} className="text-emerald-400 hover:text-emerald-300"><Check className="w-3 h-3" /></button>
                        </div>
                      ) : (
                        <div className="flex-1 flex items-start justify-between">
                          <span className="text-zinc-300 text-xs leading-relaxed">{item}</span>
                          <div className="hidden group-hover:flex items-center gap-0.5 shrink-0 ml-1">
                            <button onClick={() => setEditingOpp(idx)} className="text-zinc-500 hover:text-zinc-300"><Edit2 className="w-2.5 h-2.5" /></button>
                            <button onClick={() => removeOpp(idx)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-2.5 h-2.5" /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <Input value={newOpp} onChange={e => setNewOpp(e.target.value)} placeholder="Add opportunity..." className="bg-zinc-900 border-zinc-800 text-xs h-6 py-0 flex-1 placeholder:text-zinc-700" data-testid="add-opp-input" onKeyDown={e => e.key === 'Enter' && addOpp()} />
                  <button onClick={addOpp} className="text-emerald-500 hover:text-emerald-400" data-testid="add-opp-btn"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              </div>
              {/* Risks */}
              <div>
                <p className="text-xs uppercase tracking-widest text-red-400 font-manrope mb-2">Risks</p>
                <div className="space-y-1.5 max-h-[160px] overflow-y-auto">
                  {notes.risks.map((item, idx) => (
                    <div key={idx} className="flex items-start gap-1.5 group">
                      <span className="text-red-500 text-xs mt-0.5 shrink-0">&#8226;</span>
                      {editingRisk === idx ? (
                        <div className="flex-1 flex items-center gap-1">
                          <Input value={item} onChange={e => updateRisk(idx, e.target.value)} className="bg-zinc-900 border-zinc-700 text-xs h-6 py-0" autoFocus onKeyDown={e => e.key === 'Enter' && setEditingRisk(null)} />
                          <button onClick={() => setEditingRisk(null)} className="text-red-400 hover:text-red-300"><Check className="w-3 h-3" /></button>
                        </div>
                      ) : (
                        <div className="flex-1 flex items-start justify-between">
                          <span className="text-zinc-300 text-xs leading-relaxed">{item}</span>
                          <div className="hidden group-hover:flex items-center gap-0.5 shrink-0 ml-1">
                            <button onClick={() => setEditingRisk(idx)} className="text-zinc-500 hover:text-zinc-300"><Edit2 className="w-2.5 h-2.5" /></button>
                            <button onClick={() => removeRisk(idx)} className="text-zinc-500 hover:text-red-400"><Trash2 className="w-2.5 h-2.5" /></button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <div className="flex items-center gap-1 mt-2">
                  <Input value={newRisk} onChange={e => setNewRisk(e.target.value)} placeholder="Add risk..." className="bg-zinc-900 border-zinc-800 text-xs h-6 py-0 flex-1 placeholder:text-zinc-700" data-testid="add-risk-input" onKeyDown={e => e.key === 'Enter' && addRisk()} />
                  <button onClick={addRisk} className="text-red-500 hover:text-red-400" data-testid="add-risk-btn"><Plus className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* BOTTOM RIGHT: Revenue by Project Type */}
        <Card className="bg-zinc-950 border border-zinc-700/50 rounded-xl" data-testid="revenue-type-card">
          <CardContent className="p-4">
            <div style={{ width: '100%', height: 200 }}>
              <ResponsiveContainer>
                <BarChart data={typeChartData} margin={{ top: 5, right: 10, left: 10, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis dataKey="type" tick={{ fill: '#a1a1aa', fontSize: 11, fontFamily: 'Manrope' }} axisLine={{ stroke: '#3f3f46' }} />
                  <YAxis tick={{ fill: '#a1a1aa', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={{ stroke: '#3f3f46' }} tickFormatter={v => `$${(v / 1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" name="Revenue" radius={[4, 4, 0, 0]}>
                    {typeChartData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="text-center mt-3">
              <h3 className="font-manrope font-bold text-sm text-zinc-400">{getFYLabel(selectedFY)} Revenue by Type</h3>
              <p className="font-mono text-lg font-bold text-white mt-1">{formatCurrency(totalRevenue)}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
